import React from 'react';
import styled from 'styled-components';
import { GoAlert, GoInfo, GoX } from 'react-icons/go';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.card || '#1e1e1e'};
  border: 1px solid ${({ theme }) => theme.border || '#333'};
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  max-width: 500px;
  width: 90%;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.border || '#333'};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: ${({ variant }) => {
    switch (variant) {
      case 'danger': return 'rgba(239, 68, 68, 0.1)';
      case 'warning': return 'rgba(251, 191, 36, 0.1)';
      default: return 'rgba(59, 130, 246, 0.1)';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'danger': return '#ef4444';
      case 'warning': return '#fbbf24';
      default: return '#3b82f6';
    }
  }};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text || '#fff'};
  flex: 1;
`;

const Body = styled.div`
  padding: 24px;
  color: ${({ theme }) => theme.text || '#fff'};
  line-height: 1.6;
  font-size: 14px;
`;

const Message = styled.p`
  margin: 0 0 16px 0;
  opacity: 0.9;
`;

const WarningBox = styled.div`
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-top: 16px;
  display: flex;
  gap: 10px;
  align-items: start;

  svg {
    color: #fbbf24;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #fbbf24;
    line-height: 1.5;
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.border || '#333'};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 100px;

  ${({ variant }) => {
    switch (variant) {
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover {
            background: #dc2626;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          }
        `;
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover {
            background: #2563eb;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      default:
        return `
          background: transparent;
          color: ${({ theme }) => theme.text || '#fff'};
          border: 1px solid ${({ theme }) => theme.border || '#333'};
          &:hover {
            background: rgba(255, 255, 255, 0.05);
          }
        `;
    }
  }}

  &:active {
    transform: translateY(0);
  }
`;

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  warning,
  variant = 'info',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  confirmVariant = 'primary'
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (variant) {
      case 'danger':
      case 'warning':
        return <GoAlert />;
      default:
        return <GoInfo />;
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <IconWrapper variant={variant}>
            {getIcon()}
          </IconWrapper>
          <Title>{title}</Title>
        </Header>

        <Body>
          <Message>{message}</Message>
          
          {warning && (
            <WarningBox>
              <GoAlert />
              <p>{warning}</p>
            </WarningBox>
          )}
        </Body>

        <Footer>
          <Button variant="secondary" onClick={onClose}>
            {cancelText}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm}>
            {confirmText}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ConfirmModal;
